import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Badge } from './badge';
import { Button } from './button';
import { Input } from './input';
import { Check, ChevronsUpDown, X, BookMarked, Type } from 'lucide-react';

/**
 * Multi-select for academic standards.
 * Supports both dropdown selection AND pasting space-separated standard codes.
 * props:
 *  - standards: [{id, code, description, subject, grade_level}]
 *  - value: string[] of selected standard ids
 *  - onChange: (ids: string[]) => void
 */
export const StandardsMultiSelect = ({ standards = [], value = [], onChange, testId = 'standards-select' }) => {
  const [open, setOpen] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const selected = standards.filter(s => value.includes(s.id));

  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter(v => v !== id));
    else onChange([...value, id]);
  };

  // Parse space-separated standard codes and add matching standards
  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    
    const codes = textInput.trim().split(/\s+/); // Split by one or more spaces
    const matchedIds = [];
    const notFound = [];
    
    codes.forEach(code => {
      // Find standard by code (case-insensitive)
      const match = standards.find(s => s.code.toLowerCase() === code.toLowerCase());
      if (match && !value.includes(match.id)) {
        matchedIds.push(match.id);
      } else if (!match) {
        notFound.push(code);
      }
    });
    
    if (matchedIds.length > 0) {
      onChange([...value, ...matchedIds]);
    }
    
    // Clear input but keep codes that weren't found so user can see/fix them
    setTextInput(notFound.join(' '));
  };

  return (
    <div>
      {/* Toggle between dropdown and text input */}
      <div className="flex items-center gap-2 mb-2">
        <Button
          type="button"
          variant={showTextInput ? "outline" : "default"}
          size="sm"
          onClick={() => setShowTextInput(false)}
          className={`text-xs ${!showTextInput ? 'bg-indigo-600 text-white' : ''}`}
        >
          <ChevronsUpDown className="w-3 h-3 mr-1" />
          Select
        </Button>
        <Button
          type="button"
          variant={showTextInput ? "default" : "outline"}
          size="sm"
          onClick={() => setShowTextInput(true)}
          className={`text-xs ${showTextInput ? 'bg-indigo-600 text-white' : ''}`}
        >
          <Type className="w-3 h-3 mr-1" />
          Paste List
        </Button>
      </div>

      {showTextInput ? (
        /* Text input mode - paste space-separated codes */
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Paste standard codes separated by spaces (e.g., 4.NF.1 4.NF.2 4.NF.3)"
              className="flex-1 text-sm font-mono"
              data-testid={`${testId}-text-input`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleTextSubmit();
                }
              }}
            />
            <Button
              type="button"
              onClick={handleTextSubmit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid={`${testId}-add-btn`}
            >
              Add
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Enter standard codes separated by single spaces, then click Add or press Enter.
          </p>
        </div>
      ) : (
        /* Dropdown selection mode */
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              className="w-full justify-between font-normal"
              data-testid={`${testId}-trigger`}
            >
              <span className="flex items-center gap-2 text-slate-600">
                <BookMarked className="w-4 h-4" />
                {value.length ? `${value.length} standard${value.length > 1 ? 's' : ''} selected` : 'Select standards…'}
              </span>
              <ChevronsUpDown className="w-4 h-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start">
            <Command>
              <CommandInput placeholder="Search standards…" />
              <CommandList>
                <CommandEmpty>
                  {standards.length === 0
                    ? 'No standards available for your state. Set your state in Settings, or ask your admin to add standards.'
                    : 'No matching standards.'}
                </CommandEmpty>
                <CommandGroup>
                  {standards.map(s => {
                    const isSel = value.includes(s.id);
                    return (
                      <CommandItem
                        key={s.id}
                        value={`${s.code} ${s.description} ${s.subject || ''}`}
                        onSelect={() => toggle(s.id)}
                        data-testid={`${testId}-option-${s.id}`}
                      >
                        <Check className={`mr-2 h-4 w-4 ${isSel ? 'opacity-100' : 'opacity-0'}`} />
                        <div className="flex flex-col">
                          <span className="font-mono text-xs font-semibold">{s.code}</span>
                          <span className="text-xs text-slate-500 line-clamp-1">{s.description}</span>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testId}-chips`}>
          {selected.map(s => (
            <Badge key={s.id} className="bg-indigo-100 text-indigo-700 border-indigo-200 flex items-center gap-1">
              {s.code}
              <button type="button" onClick={() => toggle(s.id)} className="hover:text-indigo-900" data-testid={`${testId}-remove-${s.id}`}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default StandardsMultiSelect;
